// Bulk import of assets (CR_20260920_Asset_Register phase C, A7–A10): a CSV reader matched to the web's writer
// (lib/exports.ts — an export round-trips), the column contract, and the same row checks the server runs in
// public.import_assets — so the preview flags exactly what the server would refuse. The server re-checks everything
// (the client is never trusted) and writes all rows or none (Q3).

/** The importable columns (A9). Status, zone, tag and value are not: each has its own path and guard. */
export const IMPORT_COLUMNS = [
  'asset_code',
  'category',
  'description',
  'make_model',
  'serial',
  'custody_site',
] as const;
export type ImportColumn = (typeof IMPORT_COLUMNS)[number];
export type ImportRow = Record<ImportColumn, string>;

/** Same rule as the assets.asset_code check constraint. */
export const ASSET_CODE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,39}$/;
export const IMPORT_MAX_ROWS = 2000;
const MAX_TEXT = 500;

/** Error codes shared with public.import_assets; the web has one message per code. */
export type ImportErrorCode =
  | 'code_missing'
  | 'code_invalid'
  | 'code_duplicate'
  | 'code_taken'
  | 'category_unknown'
  | 'site_unknown'
  | 'site_ambiguous'
  | 'too_long';

export interface ImportIssue {
  /** 1-based data row (the header is not counted) — the server numbers rows the same way */
  row: number;
  code: ImportErrorCode;
  field: ImportColumn;
  value?: string | null;
}

/** RFC 4180 CSV: quoted fields, doubled quotes, CRLF or LF, a leading BOM. Blank lines are dropped. */
export function parseCsv(text: string): string[][] {
  const src = text.startsWith('﻿') ? text.slice(1) : text;
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const c = src[i]!;
    if (quoted) {
      if (c === '"') {
        if (src[i + 1] === '"') {
          cell += '"';
          i++;
        } else quoted = false;
      } else cell += c;
    } else if (c === '"' && cell === '') quoted = true;
    else if (c === ',') {
      row.push(cell);
      cell = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && src[i + 1] === '\n') i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else cell += c;
  }
  if (cell !== '' || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((v) => v.trim() !== ''));
}

// the writer's formula-injection guard prefixes ' to text starting = + - @ tab CR; take it off again
function unguard(v: string): string {
  return /^'[=+\-@\t\r]/.test(v) ? v.slice(1) : v;
}

export interface ReadResult {
  rows: ImportRow[];
  /** header problems: a missing asset_code column, columns we do not know (named), a file with no rows */
  problems: { kind: 'no_code_column' | 'unknown_column' | 'empty' | 'too_many'; column?: string }[];
}

/** Header row → column names (case and spaces ignored, "Asset code" = asset_code); then one ImportRow per line. */
export function readImport(text: string): ReadResult {
  const table = parseCsv(text);
  const problems: ReadResult['problems'] = [];
  if (table.length < 2) return { rows: [], problems: [{ kind: 'empty' }] };
  const header = table[0]!.map((h) =>
    h
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_'),
  );
  const index = new Map<ImportColumn, number>();
  header.forEach((h, i) => {
    if ((IMPORT_COLUMNS as readonly string[]).includes(h)) index.set(h as ImportColumn, i);
    else if (h) problems.push({ kind: 'unknown_column', column: table[0]![i]!.trim() });
  });
  if (!index.has('asset_code')) problems.push({ kind: 'no_code_column' });
  const body = table.slice(1);
  if (body.length > IMPORT_MAX_ROWS) problems.push({ kind: 'too_many' });
  const rows = body.map((cells) => {
    const r = {} as ImportRow;
    for (const col of IMPORT_COLUMNS) {
      const i = index.get(col);
      r[col] = i === undefined ? '' : unguard((cells[i] ?? '').trim());
    }
    return r;
  });
  return { rows, problems };
}

export interface ImportContext {
  /** the company's category names */
  categories: readonly string[];
  /** names of the active sites the importer can reach */
  sites: readonly string[];
  /** codes already registered in the company (exact, as the unique index compares them) */
  taken: ReadonlySet<string>;
}

/** The server's checks, row by row (public.import_assets), for the preview. */
export function validateImport(rows: readonly ImportRow[], ctx: ImportContext): ImportIssue[] {
  const issues: ImportIssue[] = [];
  const lower = (s: string) => s.toLowerCase();
  const seen = new Map<string, number>();
  for (const r of rows) {
    if (r.asset_code) seen.set(lower(r.asset_code), (seen.get(lower(r.asset_code)) ?? 0) + 1);
  }
  const cats = new Set(ctx.categories.map(lower));
  const siteCount = new Map<string, number>();
  for (const s of ctx.sites) siteCount.set(lower(s), (siteCount.get(lower(s)) ?? 0) + 1);

  rows.forEach((r, i) => {
    const row = i + 1;
    const code = r.asset_code;
    if (!code) issues.push({ row, code: 'code_missing', field: 'asset_code' });
    else {
      if (!ASSET_CODE.test(code))
        issues.push({ row, code: 'code_invalid', field: 'asset_code', value: code });
      if ((seen.get(lower(code)) ?? 0) > 1)
        issues.push({ row, code: 'code_duplicate', field: 'asset_code', value: code });
      if (ctx.taken.has(code))
        issues.push({ row, code: 'code_taken', field: 'asset_code', value: code });
    }
    if (r.category && !cats.has(lower(r.category)))
      issues.push({ row, code: 'category_unknown', field: 'category', value: r.category });
    if (r.custody_site) {
      const n = siteCount.get(lower(r.custody_site)) ?? 0;
      if (n === 0)
        issues.push({ row, code: 'site_unknown', field: 'custody_site', value: r.custody_site });
      if (n > 1)
        issues.push({ row, code: 'site_ambiguous', field: 'custody_site', value: r.custody_site });
    }
    for (const f of ['description', 'make_model', 'serial'] as const) {
      if (r[f].length > MAX_TEXT) issues.push({ row, code: 'too_long', field: f });
    }
  });
  return issues;
}

/** The downloadable template: the header and one example row. */
export function importTemplate(example: Partial<ImportRow> = {}): string {
  const row: ImportRow = {
    asset_code: 'SP-001',
    category: 'Steel plate',
    description: 'Steel plate 1.5 x 3 m',
    make_model: '',
    serial: '',
    custody_site: 'Shah Alam Yard',
    ...example,
  };
  const cell = (v: string) => (/[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  return `${IMPORT_COLUMNS.join(',')}\r\n${IMPORT_COLUMNS.map((c) => cell(row[c])).join(',')}\r\n`;
}
